/*
 * Banyomda — PDP Product Image Optical Engine v5.22
 *
 * Engineering intent:
 * - The supplier source image is never edited.
 * - Only the CURRENT PRIMARY media item may receive optical normalization.
 * - Deterministic media-ID calibration is authoritative when present.
 * - Runtime pixel analysis is a bounded fallback, not a visual guess.
 * - Variant media reordering, section rendering and image src/srcset changes
 *   are treated as lifecycle events.
 *
 * v5.22 preserves the v5.21 optical architecture and hardens lifecycle/performance edges:
 * v5.17 removed provisional handle calibration (correct), but the remaining
 * runtime analyzer rejected legitimate white-on-white products too often because
 * confidence was tied to foreground pixel count. The result was scale=1 on
 * sparse/white products such as wall-hung toilets. v5.21 makes confidence
 * depend on segmentation agreement, border cleanliness and geometry plausibility.
 */
(() => {
  'use strict';

  const ENGINE_KEY = '__BanyomdaOpticalEngineV522';
  if (window[ENGINE_KEY]) return;
  window[ENGINE_KEY] = true;

  const SELECTOR = '[data-byd-optical-media="true"] .byd-optical-media-frame img';
  const SAMPLE_SIZE = 360;
  const MIN_OBJECT_PIXELS = 18;
  const MAX_SCALE = 1.18;
  const MIN_SAFE_SCALE = 0.25;
  const SAFE_EDGE_MARGIN = 0.10;
  const ANALYSIS_TIMEOUT = 1600;
  const MIN_RUNTIME_CONFIDENCE = 0.56;
  const IMAGE_EVENT_DEBOUNCE = 32;

  const DEBUG = (() => {
    try {
      return new URLSearchParams(window.location.search).has('byd-optical-debug');
    } catch (_error) {
      return false;
    }
  })();

  /*
   * Occupancy is intentionally family-specific. The value is not a hard-coded
   * SKU scale; it is the desired visible foreground density after the actual
   * source-image geometry has been measured.
   */
  const PROFILES = {
    default: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    faucet: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    tall: { target: 0.72, min: 0.66, max: 0.78, cap: 1.10 },
    kitchen: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    'kitchen-spiral': { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    wall: { target: 0.72, min: 0.66, max: 0.78, cap: 1.10 },
    'toilet-wall': { target: 0.72, min: 0.66, max: 0.78, cap: 1.08 },
    'toilet-floor': { target: 0.72, min: 0.66, max: 0.78, cap: 1.08 },
    sink: { target: 0.72, min: 0.66, max: 0.78, cap: 1.08 },
    shower: { target: 0.74, min: 0.68, max: 0.80, cap: 1.16 },
    'shower-tall': { target: 0.76, min: 0.70, max: 0.82, cap: 1.16 },
    'shower-panel': { target: 0.74, min: 0.68, max: 0.80, cap: 1.14 },
    'tub-faucet': { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    reservoir: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    accessory: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
    furniture: { target: 0.70, min: 0.64, max: 0.76, cap: 1.08 },
  };

  const analysisCache = new WeakMap();
  const pending = new WeakMap();
  // Monotonic per-image generation prevents an older async analysis from
  // applying after a newer lifecycle event has superseded it.
  const normalizeGeneration = new WeakMap();
  const loadTimers = new WeakMap();
  let observer = null;
  let flushScheduled = false;
  const queuedGalleries = new Set();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const raf = (fn) => {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(fn);
    else window.setTimeout(fn, 0);
  };

  const timeoutPromise = (promise, ms) => {
    let timeoutId = null;
    const timeout = new Promise((resolve) => {
      timeoutId = window.setTimeout(() => resolve(null), ms);
    });
    return Promise.race([promise, timeout]).finally(() => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    });
  };

  function luminance(r, g, b) {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function distance(r, g, b, bg) {
    const dr = r - bg.r;
    const dg = g - bg.g;
    const db = b - bg.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function median(values) {
    if (!values.length) return 0;
    const sorted = values.slice().sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }

  function percentile(values, p) {
    if (!values.length) return 0;
    const sorted = values.slice().sort((a, b) => a - b);
    const index = clamp(Math.round((sorted.length - 1) * p), 0, sorted.length - 1);
    return sorted[index];
  }

  const TALL_KIT_RE = /banyo-seti|banyo seti|ankastre-banyo-seti|ankastre banyo seti|dus-seti|duş seti|duş kolon|dus kolon|dus-kolonu|yağmurlama|yagmurlama|3-yollu|3 yollu|tepe-dus|tepe duş|el-dusu|el duşu/;

  function pageCatalogText() {
    const handle = document.querySelector('[data-byd-product-handle]')?.dataset.bydProductHandle || '';
    const title = document.querySelector('.product__title')?.textContent || '';
    return `${handle} ${title}`.toLowerCase();
  }

  function correctedFamily(requested) {
    const text = pageCatalogText();
    if (TALL_KIT_RE.test(text)) return 'shower-tall';
    return requested || 'default';
  }

  function applyPackshotStage(img) {
    const host = img.closest('[data-byd-optical-family]');
    if (!host || !img.naturalWidth || !img.naturalHeight) return;

    const ratio = img.naturalWidth / img.naturalHeight;
    let packshot = 'square';
    if (ratio >= 1.18) packshot = 'landscape';
    else if (ratio <= 0.86) packshot = 'portrait';

    const family = host.dataset.bydOpticalFamily || 'default';
    if (
      packshot === 'landscape' &&
      (family === 'shower-tall' || family === 'shower' || family === 'shower-panel' || family === 'tall')
    ) {
      packshot = 'square';
    }

    host.dataset.bydPackshot = packshot;
  }

  function getFamily(img) {
    const host = img.closest('[data-byd-optical-family]');
    const family = correctedFamily(host?.dataset.bydOpticalFamily || 'default');
    if (host && host.dataset.bydOpticalFamily !== family) host.dataset.bydOpticalFamily = family;
    return family;
  }

  function getProfile(img) {
    return PROFILES[getFamily(img)] || PROFILES.default;
  }

  function getGallery(img) {
    return img.closest('media-gallery');
  }

  function getMediaItem(img) {
    return img.closest('[data-byd-optical-media="true"]');
  }

  function getActiveMediaId(gallery) {
    if (!gallery) return null;

    const explicit = gallery.dataset.activeMediaId;
    if (explicit) {
      const explicitItem = [...gallery.querySelectorAll('[data-byd-optical-media="true"]')].find(
        (item) => String(item.dataset.mediaId || '') === String(explicit)
      );
      if (explicitItem) return String(explicit);
    }

    const flagged = gallery.querySelector('[data-byd-optical-media="true"][data-byd-optical-active="true"]');
    if (flagged?.dataset.mediaId) return String(flagged.dataset.mediaId);

    const swiperActive = gallery.querySelector('.product__media-list > .product__media-item.swiper-slide-active[data-byd-optical-media="true"]');
    if (swiperActive?.dataset.mediaId) return String(swiperActive.dataset.mediaId);

    const first = gallery.querySelector('.product__media-list > .product__media-item[data-byd-optical-media="true"]');
    return first?.dataset.mediaId ? String(first.dataset.mediaId) : null;
  }

  function isActiveItem(item) {
    const gallery = item?.closest('media-gallery');
    if (!item || !gallery) return false;
    const activeId = getActiveMediaId(gallery);
    return Boolean(activeId && String(item.dataset.mediaId || '') === activeId);
  }

  function resetOpticalStyle(img) {
    if (!img) return;
    img.style.removeProperty('--byd-optical-scale');
    img.style.removeProperty('--byd-optical-origin-x');
    img.style.removeProperty('--byd-optical-origin-y');
    img.style.removeProperty('--byd-optical-shift-x');
    img.style.removeProperty('--byd-optical-shift-y');
    delete img.dataset.bydOpticalNormalized;
    delete img.dataset.bydOpticalState;
    delete img.dataset.bydOpticalConfidence;
    delete img.dataset.bydOpticalOccupancy;
    delete img.dataset.bydOpticalSource;
    delete img.dataset.bydOpticalCalibration;
    delete img.dataset.bydOpticalProfile;
  }

  function syncPrimaryState(gallery) {
    if (!gallery) return null;

    const activeId = getActiveMediaId(gallery);
    const items = gallery.querySelectorAll('[data-byd-optical-media="true"]');
    let activeItem = null;

    items.forEach((item) => {
      const active = Boolean(activeId && String(item.dataset.mediaId || '') === activeId);
      item.dataset.bydOpticalActive = active ? 'true' : 'false';
      item.dataset.bydOpticalHero = active ? 'true' : 'false';
      if (active) activeItem = item;

      const img = item.querySelector('.byd-optical-media-frame img');
      if (img && !active) resetOpticalStyle(img);
    });

    return activeItem;
  }

  function sampleBorder(data, width, height) {
    const samples = [];
    const push = (x, y) => {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 245) return;
      samples.push({ r: data[i], g: data[i + 1], b: data[i + 2] });
    };

    const edgeStepX = Math.max(1, Math.floor(width / 24));
    const edgeStepY = Math.max(1, Math.floor(height / 24));
    for (let x = 0; x < width; x += edgeStepX) {
      push(x, 0);
      push(x, height - 1);
      push(x, Math.min(height - 1, 2));
      push(x, Math.max(0, height - 3));
    }
    for (let y = 0; y < height; y += edgeStepY) {
      push(0, y);
      push(width - 1, y);
      push(Math.min(width - 1, 2), y);
      push(Math.max(0, width - 3), y);
    }

    if (!samples.length) return null;

    return {
      r: Math.round(median(samples.map((s) => s.r))),
      g: Math.round(median(samples.map((s) => s.g))),
      b: Math.round(median(samples.map((s) => s.b))),
      spread: Math.max(
        percentile(samples.map((s) => s.r), 0.9) - percentile(samples.map((s) => s.r), 0.1),
        percentile(samples.map((s) => s.g), 0.9) - percentile(samples.map((s) => s.g), 0.1),
        percentile(samples.map((s) => s.b), 0.9) - percentile(samples.map((s) => s.b), 0.1)
      ),
    };
  }

  function bboxFromAlpha(data, width, height, threshold) {
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    let pixels = 0;

    for (let y = 1; y < height - 1; y += 1) {
      for (let x = 1; x < width - 1; x += 1) {
        const i = (y * width + x) * 4;
        if (data[i + 3] < threshold) continue;
        pixels += 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }

    if (pixels < MIN_OBJECT_PIXELS || maxX < minX || maxY < minY) return null;

    return {
      minX, minY, maxX, maxY,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
      pixels,
    };
  }

  function bboxFromOpaqueMask(data, width, height, background, thresholds) {
    const rowHits = new Uint16Array(height);
    const colHits = new Uint16Array(width);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    let pixels = 0;
    let borderHits = 0;

    for (let y = 1; y < height - 1; y += 1) {
      for (let x = 1; x < width - 1; x += 1) {
        const i = (y * width + x) * 4;
        if (data[i + 3] < 18) continue;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const colorDelta = distance(r, g, b, background);
        const lumaDelta = Math.abs(luminance(r, g, b) - luminance(background.r, background.g, background.b));

        const left = i - 4;
        const right = i + 4;
        const up = i - width * 4;
        const down = i + width * 4;
        const gx = Math.abs(data[right] - data[left])
          + Math.abs(data[right + 1] - data[left + 1])
          + Math.abs(data[right + 2] - data[left + 2]);
        const gy = Math.abs(data[down] - data[up])
          + Math.abs(data[down + 1] - data[up + 1])
          + Math.abs(data[down + 2] - data[up + 2]);
        const edge = (gx + gy) / 6;

        /*
         * Edge-only pixels are allowed only when they are materially stronger
         * than the border noise. This is critical for white ceramic on white.
         */
        const foreground = colorDelta > thresholds.colorT
          || lumaDelta > thresholds.lumaT
          || edge > thresholds.edgeT;

        if (!foreground) continue;

        pixels += 1;
        rowHits[y] += 1;
        colHits[x] += 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);

        if (
          x < width * 0.03 || x > width * 0.97
          || y < height * 0.03 || y > height * 0.97
        ) {
          borderHits += 1;
        }
      }
    }

    if (pixels < MIN_OBJECT_PIXELS || maxX < minX || maxY < minY) return null;

    /*
     * Reject isolated compression noise by requiring a measurable run in both
     * axes. Thin faucets survive because the threshold is proportional to the
     * source width/height rather than object area.
     */
    const rowThreshold = Math.max(2, Math.round(width * 0.003));
    const colThreshold = Math.max(2, Math.round(height * 0.003));

    let strongMinX = width;
    let strongMinY = height;
    let strongMaxX = -1;
    let strongMaxY = -1;

    for (let y = 0; y < height; y += 1) {
      if (rowHits[y] >= rowThreshold) {
        strongMinY = Math.min(strongMinY, y);
        strongMaxY = Math.max(strongMaxY, y);
      }
    }
    for (let x = 0; x < width; x += 1) {
      if (colHits[x] >= colThreshold) {
        strongMinX = Math.min(strongMinX, x);
        strongMaxX = Math.max(strongMaxX, x);
      }
    }

    if (strongMaxX < strongMinX || strongMaxY < strongMinY) return null;

    const bbox = {
      minX: strongMinX,
      minY: strongMinY,
      maxX: strongMaxX,
      maxY: strongMaxY,
    };
    bbox.width = bbox.maxX - bbox.minX + 1;
    bbox.height = bbox.maxY - bbox.minY + 1;
    bbox.pixels = pixels;
    bbox.borderRatio = borderHits / pixels;

    return bbox;
  }

  function geometryFromBbox(bbox, width, height) {
    const occupancy = Math.max(bbox.width / width, bbox.height / height);
    const centerX = (bbox.minX + bbox.maxX) / 2 / width;
    const centerY = (bbox.minY + bbox.maxY) / 2 / height;

    return {
      occupancy,
      bbox: {
        ...bbox,
        centerX,
        centerY,
      },
    };
  }

  function evaluateCandidate(candidate, width, height, source, backgroundSpread = 0) {
    if (!candidate) return null;
    const geometry = geometryFromBbox(candidate, width, height);
    const { occupancy, bbox } = geometry;

    if (!Number.isFinite(occupancy) || occupancy < 0.035 || occupancy > 0.94) return null;

    const edgeMargins = Math.min(
      bbox.minX / width,
      bbox.minY / height,
      (width - 1 - bbox.maxX) / width,
      (height - 1 - bbox.maxY) / height
    );

    const centerPenalty = Math.max(
      Math.abs(bbox.centerX - 0.5),
      Math.abs(bbox.centerY - 0.5)
    );

    const borderCleanliness = 1 - clamp((bbox.borderRatio || 0) / 0.16, 0, 1);
    const edgeSafety = clamp(edgeMargins / 0.08, 0, 1);
    const backgroundQuality = clamp(1 - backgroundSpread / 32, 0, 1);
    const geometryQuality = 1 - clamp(centerPenalty / 0.45, 0, 1);

    return {
      ...geometry,
      source,
      borderCleanliness,
      edgeSafety,
      backgroundQuality,
      geometryQuality,
      score: (
        borderCleanliness * 0.32
        + edgeSafety * 0.18
        + backgroundQuality * 0.15
        + geometryQuality * 0.10
        + 0.25
      ),
    };
  }

  function analyze(canvas, ctx) {
    const width = canvas.width;
    const height = canvas.height;
    const data = ctx.getImageData(0, 0, width, height).data;

    let transparentPixels = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 245) transparentPixels += 1;
    }
    const transparencyRatio = transparentPixels / (width * height);

    if (transparencyRatio > 0.008) {
      const alphaCandidates = [8, 18, 40, 80]
        .map((threshold) => bboxFromAlpha(data, width, height, threshold))
        .filter(Boolean);

      if (alphaCandidates.length >= 2) {
        const bbox = {
          minX: Math.round(median(alphaCandidates.map((r) => r.minX))),
          minY: Math.round(median(alphaCandidates.map((r) => r.minY))),
          maxX: Math.round(median(alphaCandidates.map((r) => r.maxX))),
          maxY: Math.round(median(alphaCandidates.map((r) => r.maxY))),
        };

        const spreads = [
          Math.max(...alphaCandidates.map((r) => r.minX)) - Math.min(...alphaCandidates.map((r) => r.minX)),
          Math.max(...alphaCandidates.map((r) => r.maxX)) - Math.min(...alphaCandidates.map((r) => r.maxX)),
          Math.max(...alphaCandidates.map((r) => r.minY)) - Math.min(...alphaCandidates.map((r) => r.minY)),
          Math.max(...alphaCandidates.map((r) => r.maxY)) - Math.min(...alphaCandidates.map((r) => r.maxY)),
        ];

        const stability = 1 - clamp(Math.max(...spreads) / Math.max(width, height) * 4, 0, 1);
        const result = evaluateCandidate(
          { ...bbox, borderRatio: 0 },
          width,
          height,
          'alpha',
          0
        );

        if (result) {
          result.confidence = clamp(result.score * 0.72 + stability * 0.28, 0, 1);
          return result;
        }
      }
    }

    const background = sampleBorder(data, width, height);
    if (!background || luminance(background.r, background.g, background.b) < 0.55) {
      return null;
    }

    const thresholds = [
      { colorT: 7, lumaT: 0.018, edgeT: 8 },
      { colorT: 11, lumaT: 0.028, edgeT: 11 },
      { colorT: 17, lumaT: 0.042, edgeT: 14 },
      { colorT: 24, lumaT: 0.060, edgeT: 18 },
    ];

    const candidates = thresholds
      .map((threshold) => bboxFromOpaqueMask(data, width, height, background, threshold))
      .map((bbox) => evaluateCandidate(bbox, width, height, 'background', background.spread))
      .filter(Boolean);

    if (candidates.length < 2) return null;

    /*
     * Prefer the median geometry, but only after measuring cross-threshold
     * agreement. This avoids the v5.17 failure where one noisy threshold could
     * make the entire confidence decision depend on foreground pixel count.
     */
    const bbox = {
      minX: Math.round(median(candidates.map((r) => r.bbox.minX))),
      minY: Math.round(median(candidates.map((r) => r.bbox.minY))),
      maxX: Math.round(median(candidates.map((r) => r.bbox.maxX))),
      maxY: Math.round(median(candidates.map((r) => r.bbox.maxY))),
    };

    const spread = Math.max(
      Math.max(...candidates.map((r) => r.bbox.minX)) - Math.min(...candidates.map((r) => r.bbox.minX)),
      Math.max(...candidates.map((r) => r.bbox.maxX)) - Math.min(...candidates.map((r) => r.bbox.maxX)),
      Math.max(...candidates.map((r) => r.bbox.minY)) - Math.min(...candidates.map((r) => r.bbox.minY)),
      Math.max(...candidates.map((r) => r.bbox.maxY)) - Math.min(...candidates.map((r) => r.bbox.maxY))
    );

    const agreement = 1 - clamp(spread / Math.max(width, height) * 5, 0, 1);
    const best = candidates.slice().sort((a, b) => b.score - a.score)[0];
    const result = evaluateCandidate(bbox, width, height, 'background', background.spread);

    if (!result) return null;

    result.confidence = clamp(
      median(candidates.map((candidate) => candidate.score)) * 0.55
      + agreement * 0.35
      + best.backgroundQuality * 0.10,
      0,
      1
    );

    return result;
  }

  function createAnalysisSurface(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return ctx ? { canvas, ctx } : null;
  }

  async function measure(img) {
    if (!img?.naturalWidth || !img?.naturalHeight) return null;

    const scale = Math.min(1, SAMPLE_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(96, Math.round(img.naturalWidth * scale));
    const height = Math.max(96, Math.round(img.naturalHeight * scale));

    /*
     * IMPORTANT: a canvas becomes permanently origin-unclean after a
     * cross-origin draw. Reusing that canvas for the CORS retry is therefore
     * invalid. v5.21 did exactly that, which made the fallback analyzer fail
     * even when the second anonymous-CORS load was perfectly usable.
     */
    const directSurface = createAnalysisSurface(width, height);
    if (!directSurface) return null;

    try {
      directSurface.ctx.drawImage(img, 0, 0, width, height);
      const direct = analyze(directSurface.canvas, directSurface.ctx);
      if (direct) return direct;
    } catch (_directError) {
      // Expected for a non-CORS CDN response. Continue with a clean surface.
    }

    const source = new Image();
    source.decoding = 'async';
    source.crossOrigin = 'anonymous';
    source.src = img.currentSrc || img.src;

    const loaded = source.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          source.onload = resolve;
          source.onerror = resolve;
        });

    await timeoutPromise(loaded, ANALYSIS_TIMEOUT);
    if (!source.complete || !source.naturalWidth) return null;

    const corsSurface = createAnalysisSurface(width, height);
    if (!corsSurface) return null;

    try {
      corsSurface.ctx.drawImage(source, 0, 0, width, height);
      return analyze(corsSurface.canvas, corsSurface.ctx);
    } catch (_error) {
      return null;
    }
  }

  function getCalibrationNode(img) {
    const host = img.closest('[data-byd-optical-family]');
    const sectionId = host?.dataset.bydOpticalSectionId;
    const productId = host?.dataset.bydOpticalProductId;
    const nodes = document.querySelectorAll('[data-byd-optical-calibration]');

    for (const node of nodes) {
      if (sectionId && node.dataset.bydOpticalSectionId === sectionId) return node;
      if (!sectionId && productId && node.dataset.bydOpticalProductId === productId) return node;
    }
    return null;
  }

  function normalizeCalibrationEntry(entry, source) {
    if (!entry || typeof entry !== 'object') return null;

    const scale = Number(entry.scale);
    if (!Number.isFinite(scale) || scale <= 0) return null;

    const originX = Number(entry.origin_x);
    const originY = Number(entry.origin_y);
    const shiftX = Number(entry.shift_x);
    const shiftY = Number(entry.shift_y);
    const confidence = Number(entry.confidence);
    const safeScaleMax = Number(entry.safe_scale_max);

    return {
      scale: clamp(scale, 0.25, MAX_SCALE),
      originX: Number.isFinite(originX) ? clamp(originX, 0, 100) : 50,
      originY: Number.isFinite(originY) ? clamp(originY, 0, 100) : 50,
      shiftX: Number.isFinite(shiftX) ? clamp(shiftX, -12, 12) : 0,
      shiftY: Number.isFinite(shiftY) ? clamp(shiftY, -12, 12) : 0,
      confidence: Number.isFinite(confidence) ? clamp(confidence, 0, 1) : null,
      safeScaleMax: Number.isFinite(safeScaleMax)
        ? clamp(safeScaleMax, MIN_SAFE_SCALE, MAX_SCALE)
        : null,
      source: source || entry.source || 'catalog-calibration',
      mediaId: entry.media_id ? String(entry.media_id) : null,
      revision: entry.revision ? String(entry.revision) : null,
    };
  }

  function getMetadataOverride(img) {
    const host = img.closest('[data-byd-optical-family]');
    const mediaHost = getMediaItem(img);
    const mediaId = mediaHost?.dataset.bydOpticalMediaId || mediaHost?.dataset.mediaId || '';

    const calibrationNode = getCalibrationNode(img);
    if (calibrationNode) {
      let calibration = null;
      try {
        calibration = JSON.parse(calibrationNode.textContent || '{}');
      } catch (_error) {
        calibration = null;
      }

      if (calibration && typeof calibration === 'object') {
        const mediaMap = calibration.media && typeof calibration.media === 'object'
          ? calibration.media
          : null;
        const keyed = mediaMap && mediaId ? mediaMap[String(mediaId)] : null;
        const entries = Array.isArray(calibration.entries) ? calibration.entries : [];
        const listed = entries.find((entry) => String(entry?.media_id || '') === String(mediaId));
        const entry = keyed || listed || null;

        if (entry) {
          /*
           * media_id is the authoritative stale-data boundary. `revision` is
           * an optional catalog revision token and must never be compared to
           * the Shopify media ID; doing so would invalidate otherwise valid
           * deterministic calibrations.
           */
          if (entry.media_id && mediaId && String(entry.media_id) !== String(mediaId)) return null;

          const normalized = normalizeCalibrationEntry(entry, entry.source || 'catalog-calibration');
          if (normalized) {
            if (normalized.safeScaleMax !== null) {
              normalized.scale = Math.min(normalized.scale, normalized.safeScaleMax);
            }
            return normalized;
          }
        }
      }
    }

    const scalarScale = Number(host?.dataset.bydOpticalScale);
    if (Number.isFinite(scalarScale) && scalarScale > 0) {
      return normalizeCalibrationEntry({
        scale: scalarScale,
        origin_x: Number(host?.dataset.bydOpticalOriginX),
        origin_y: Number(host?.dataset.bydOpticalOriginY),
      }, 'legacy-metafield');
    }

    return null;
  }

  function apply(img, _result) {
    resetOpticalStyle(img);
    img.style.setProperty('--byd-optical-scale', '1');
    img.style.setProperty('--byd-optical-shift-x', '0%');
    img.style.setProperty('--byd-optical-shift-y', '0%');
    img.dataset.bydOpticalState = 'passthrough';
    img.dataset.bydOpticalProfile = getFamily(img);
  }

  function getSignature(img) {
    const item = getMediaItem(img);
    return [
      item?.dataset.bydOpticalMediaId || item?.dataset.mediaId || '',
      img.currentSrc || img.src || '',
      img.naturalWidth || 0,
      img.naturalHeight || 0,
    ].join('|');
  }

  async function normalize(img) {
    if (!img || !img.isConnected) return;
    const item = getMediaItem(img);
    if (!item || !isActiveItem(item)) return;

    const signature = getSignature(img);
    const cached = analysisCache.get(img);
    if (cached?.signature === signature) {
      const hasCurrentOverride = Boolean(getMetadataOverride(img));
      if (!cached.metadataAuthoritative || hasCurrentOverride) {
        apply(img, cached.result);
        return;
      }
    }
    if (pending.has(img)) return;

    const generation = (normalizeGeneration.get(img) || 0) + 1;
    normalizeGeneration.set(img, generation);
    pending.set(img, { signature, generation });

    const isCurrentOperation = () =>
      img.isConnected &&
      isActiveItem(getMediaItem(img)) &&
      normalizeGeneration.get(img) === generation &&
      pending.get(img)?.generation === generation;

    try {
      const override = getMetadataOverride(img);
      if (override) {
        apply(img, null);
        analysisCache.set(img, { signature, result: null, metadataAuthoritative: true });
        return;
      }

      if (!img.complete || !img.naturalWidth) {
        await new Promise((resolve) => {
          const onLoad = () => resolve();
          img.addEventListener('load', onLoad, { once: true });
          window.setTimeout(resolve, 1200);
        });
      }

      if (!isCurrentOperation()) return;

      if (!img.naturalWidth) {
        resetOpticalStyle(img);
        return;
      }

      applyPackshotStage(img);
      getFamily(img);

      const result = await measure(img);
      if (!isCurrentOperation()) return;

      apply(img, result);
      analysisCache.set(img, { signature, result, metadataAuthoritative: false });
    } finally {
      if (pending.get(img)?.generation === generation) pending.delete(img);
    }
  }

  function scheduleGallery(gallery) {
    if (!gallery) return;
    const item = syncPrimaryState(gallery);
    const img = item?.querySelector('.byd-optical-media-frame img');
    if (!img) return;

    raf(() => normalize(img));
  }

  function queueGallery(gallery) {
    if (!gallery) return;
    queuedGalleries.add(gallery);
    if (flushScheduled) return;

    flushScheduled = true;
    raf(() => {
      flushScheduled = false;
      const galleries = Array.from(queuedGalleries);
      queuedGalleries.clear();
      galleries.forEach((root) => {
        if (root?.isConnected) scheduleGallery(root);
      });
    });
  }

  function scheduleImageLoad(img) {
    if (!img) return;
    const existing = loadTimers.get(img);
    if (existing) window.clearTimeout(existing);

    const timer = window.setTimeout(() => {
      loadTimers.delete(img);
      queueGallery(getGallery(img));
    }, IMAGE_EVENT_DEBOUNCE);

    loadTimers.set(img, timer);
  }

  function boot() {
    document.querySelectorAll('media-gallery').forEach(queueGallery);

    if (observer) observer.disconnect();

    document.addEventListener('byd:media-active', (event) => {
      const gallery = event.detail?.gallery || event.target?.closest?.('media-gallery');
      if (gallery) queueGallery(gallery);
    });

    observer = new MutationObserver((mutations) => {
      const galleries = new Set();

      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const gallery = mutation.target.closest?.('media-gallery');
          if (gallery) galleries.add(gallery);
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.('media-gallery')) galleries.add(node);
          node.querySelectorAll?.('media-gallery').forEach((gallery) => galleries.add(gallery));
          const gallery = node.closest?.('media-gallery');
          if (gallery) galleries.add(gallery);
        }
      }

      galleries.forEach(queueGallery);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'data-media-id', 'data-byd-optical-media-id'],
    });

    document.addEventListener('load', (event) => {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.matches(SELECTOR)) scheduleImageLoad(img);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
